import PluginUtils from './PluginUtils';
import {
  BoolPriceData,
  EnumPriceData,
  ExtraPaymentLineData,
  GTPriceData,
  MultipleTicketsPriceData,
  NumberPriceData,
  OrderData,
  OrderDetailsRequestData,
  OrderPageData,
} from '../api';

export const MULTIPLE_TICKETS_DETAILS_FIELD_NAME = 'multiple_tickets_details';

export enum PriceMode {
  CALCULATED = 'calculated',
  ZERO = 'zero',
  OVERRIDE = 'override',
}

const initialEditOrderState: EditOrderState = {
  priceMode: PriceMode.CALCULATED,
  shouldAllowPriceOverride: false,
  calculatedPrice: 0,
  extraPaymentsLines: [],
  paymentLinksRefreshKey: 0,
};

export enum VoucherError {
  NOT_FOUND = 'NOT_FOUND',
  ALREADY_USED = 'ALREADY_USED',
  EXPIRED = 'EXPIRED',
  ARCHIVED = 'ARCHIVED',
}

export enum SpecificEventError {
  NO_MORE_TICKETS = 'NO_MORE_TICKETS',
}

export type CustomTheme = {
  textColor: string;
  borderColor: string;
  backgroundColor: string;
};

export type EditOrderState = {
  priceMode: PriceMode;
  shouldAllowPriceOverride: boolean;
  calculatedPrice: number;
  extraPaymentsLines: ExtraPaymentLineData[];
  // Bumped whenever an extra payment is committed server-side, so every
  // usePriceRenderer instance refetches its payment links.
  paymentLinksRefreshKey: number;
};
type StepPricingTier = {
  quantity: number;
  price_per_unit: number;
};

type PriceData = EnumPriceData | NumberPriceData | BoolPriceData | GTPriceData | MultipleTicketsPriceData;

const calculateTieredPrice = (stepPricing: StepPricingTier[], quantity: number): number => {
  let totalPrice = 0;
  stepPricing.sort((a, b) => b.price_per_unit - a.price_per_unit);

  for (const tier of stepPricing) {
    if (quantity > 0) {
      const numItemsInTier = Math.min(quantity, tier.quantity);
      totalPrice += numItemsInTier * tier.price_per_unit;
      quantity -= numItemsInTier;
    } else {
      break;
    }
  }
  // if we still have more units, they get the last tier pricing
  if (quantity > 0) {
    totalPrice += quantity * stepPricing[stepPricing.length - 1].price_per_unit;
  }

  return totalPrice;
};

const calculateCustomPerUnitPrice = (
  {
    custom_per_unit_pricing,
    price_per_additional_value,
  }: {
    custom_per_unit_pricing?: number[];
    price_per_additional_value?: number;
  },
  quantity: number,
): number => {
  if (!custom_per_unit_pricing || !price_per_additional_value) {
    return 0;
  }
  if (custom_per_unit_pricing.length >= quantity) {
    return custom_per_unit_pricing[quantity - 1];
  }
  return (
    custom_per_unit_pricing[custom_per_unit_pricing.length - 1] +
    (quantity - custom_per_unit_pricing.length) * price_per_additional_value
  );
};
const calcNumberPrice = (price_data: NumberPriceData, order_details: any) => {
  let price = price_data.base_price;
  let additional_values = order_details[price_data.data_type] - price_data.base_value;
  if (additional_values < 0) {
    additional_values = 0;
  }
  if (price_data.tiered_pricing?.length) {
    price += calculateTieredPrice(price_data.tiered_pricing, additional_values);
  } else if (price_data.custom_per_unit_pricing?.length) {
    // calculating based on the amount of persons
    price += calculateCustomPerUnitPrice(price_data, order_details[price_data.data_type]);
  } else {
    price += additional_values * (price_data.price_per_additional_value || 0);
  }

  return price;
};

const isMatchingBoolPrice = (price_data: BoolPriceData, order_details: any) => {
  if (price_data.data_type === 'custom') {
    return order_details.custom_details[price_data.name];
  }
  return false;
};

const isMatchingGtPrice = (price_data: GTPriceData, order_details: any) => {
  if (price_data.data_type === 'custom') {
    const value = order_details.custom_details[price_data.name];
    if (value === null || value === undefined) {
      return false;
    }
    return value > price_data.value;
  }
  return false;
};

const getMatchingEnumPrice = (price_data: EnumPriceData, order_details: OrderDetailsRequestData) => {
  if (price_data.data_type === 'custom') {
    if (!price_data.name) {
      throw new Error('price_data.name is undefined in enum price');
    }
    if (order_details.custom_details[price_data.name] === undefined) {
      return price_data.options[0].price;
    }
  }
  const matchingOption = price_data.options.find((option: any) => {
    if (price_data.data_type === 'custom') {
      if (!price_data.name) {
        throw new Error('price_data.name is undefined in enum price');
      }
      return option.value === `${order_details.custom_details[price_data.name]}`;
    }

    return option.value === `${order_details[price_data.data_type]}`;
  });
  return matchingOption?.price || 0;
};

function calculateMultipleTicketsPrice(
  currentObj: MultipleTicketsPriceData,
  orderDetails: OrderDetailsRequestData,
  orderPage: OrderPageData,
) {
  const multipleTicketsData = orderDetails.custom_details[MULTIPLE_TICKETS_DETAILS_FIELD_NAME] as Record<
    string,
    number
  >;
  const activeOptions = currentObj.options?.filter((option) => option.archived !== true) || [];
  const cheapestItemPrice = Math.min(...(activeOptions.length ? activeOptions.map((o) => o.price_per_unit) : [0]));

  const minParticipants = orderPage.objects.find(
    (x) => x.type === 'multipleTickets',
    // @ts-ignore
  )?.min_total_participants;
  const minPrice = minParticipants ? minParticipants * cheapestItemPrice : undefined;
  if (!multipleTicketsData) {
    return minPrice;
  }
  let price = 0;
  for (const ticketId in multipleTicketsData) {
    if (multipleTicketsData[ticketId]) {
      const ticketQuantity = multipleTicketsData[ticketId];
      const pricePerUnit = currentObj.options?.find((option) => option.ticket_id === ticketId)?.price_per_unit || 0;
      price += pricePerUnit * ticketQuantity || 0;
    }
  }

  if (minPrice && price < minPrice) {
    return minPrice;
  }
  return price;
}

export const calcPriceObject = (
  price_data: PriceData,
  orderDetails: OrderDetailsRequestData,
  orderPage: OrderPageData,
) => {
  let currentObj: PriceData = price_data;
  let price;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (currentObj.type === 'enum') {
      const enumPrice = getMatchingEnumPrice(currentObj as EnumPriceData, orderDetails);
      if (enumPrice === 0) {
        return 0;
      }
      currentObj = enumPrice;
    } else if (currentObj.type === 'number') {
      price = calcNumberPrice(currentObj as NumberPriceData, orderDetails);
      return price;
    } else if (currentObj.type === 'bool') {
      if (isMatchingBoolPrice(currentObj as BoolPriceData, orderDetails)) {
        const boolPriceData = currentObj as BoolPriceData;
        if (boolPriceData.price) {
          currentObj = boolPriceData.price;
        } else {
          return boolPriceData.price_value;
        }
      } else {
        return 0;
      }
    } else if (currentObj.type === 'gt') {
      if (isMatchingGtPrice(currentObj as GTPriceData, orderDetails)) {
        // @ts-ignore
        if (currentObj.price) {
          // @ts-ignore
          currentObj = currentObj.price;
        } else {
          // @ts-ignore
          return currentObj.price_value;
        }
      } else {
        return 0;
      }
    } else if (currentObj.type === 'multiple_tickets_price') {
      return calculateMultipleTicketsPrice(currentObj as MultipleTicketsPriceData, orderDetails, orderPage);
    }
  }
};

export const calculatePrice = (
  orderPage: OrderPageData,
  order_details: OrderDetailsRequestData,
  date_details: any,
  shouldForceUseWeekendAdditionForVouchers?: boolean,
  depositPercentage?: number,
) => {
  const { plugins, price: priceDataList } = orderPage;
  let price = 0;

  for (const priceDataItem of priceDataList) {
    let itemPrice = calcPriceObject(priceDataItem, order_details, orderPage);

    if (depositPercentage) {
      if (priceDataItem.data_type === 'custom') {
        itemPrice = 0;
      } else {
        itemPrice *= depositPercentage / 100;
      }
    }

    price += itemPrice;
  }

  price = PluginUtils.handleWeekendAndIsraeliHolidaysPlugin(
    plugins,
    price,
    date_details.date,
    shouldForceUseWeekendAdditionForVouchers,
  );
  return price;
};

const getDefaultCustomDetails = (orderPage: any) => {
  const result = {};
  for (const customDetail of orderPage.custom_details) {
    if (customDetail.default_value) {
      // @ts-ignore
      result[customDetail.name] = customDetail.default_value;
    }
  }
  return result;
};

/** The management comment's field id, so the edit-order drawer can scroll to it. */
export const MANAGEMENT_COMMENT_FIELD_ID = 'order-management-comment';

/** The resource (table) picker's element id, so the edit-order drawer can scroll to it. */
export const RESOURCE_SELECTION_FIELD_ID = 'order-resource-selection';

/** The first customer-facing comment field the customer filled in, if any. */
const filledCustomerCommentFieldId = (order: OrderData, orderPage: OrderPageData | undefined): string | undefined =>
  orderPage?.custom_personal_details?.find(
    (detail) => detail.is_comment_field && order.custom_personal_details?.[detail.id],
  )?.id;

/** The text of the first customer-facing comment field the customer filled in, if any. */
export const customerCommentText = (order: OrderData, orderPage: OrderPageData | undefined): string | undefined => {
  const fieldId = filledCustomerCommentFieldId(order, orderPage);
  const customDetails = order.custom_personal_details as unknown as Record<string, unknown> | undefined;
  const value = fieldId ? customDetails?.[fieldId] : undefined;
  return typeof value === 'string' && value.trim() ? value : undefined;
};

/** A staff comment, or a customer-facing comment field the customer filled in. */
export const orderHasComment = (order: OrderData, orderPage: OrderPageData | undefined): boolean =>
  !!order.comment || !!filledCustomerCommentFieldId(order, orderPage);

/**
 * Where an order's comment is shown in the edit-order drawer. The staff comment wins: it is the one
 * written for the team. Customer comment fields render with the detail id as their element id.
 */
export const orderCommentFieldId = (order: OrderData, orderPage: OrderPageData | undefined): string | undefined =>
  order.comment ? MANAGEMENT_COMMENT_FIELD_ID : filledCustomerCommentFieldId(order, orderPage);

const OrderUtils = {
  getDefaultCustomDetails,
  ORDER_PAGE_TTL: 10 * 60,
  initialEditOrderState,
};

export default OrderUtils;
